/**
 * UTC storage and timezone-aware display.
 * All dates are stored in UTC. Display uses user's timezone (default IST).
 */

/** Default timezone for all users (IST). */
export const DEFAULT_TIMEZONE = 'Asia/Kolkata';

/**
 * Normalize any timestamp format to UTC milliseconds.
 * Handles: number (ms), Date, Firestore Timestamp {seconds, nanoseconds}, serialized {_seconds, _nanoseconds}.
 * @returns {number|null} UTC ms or null if invalid
 */
export function toUtcMs(value) {
  if (value == null) return null;
  if (typeof value === 'number' && !isNaN(value)) return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value.toMillis === 'function') return value.toMillis();
  const sec = value.seconds ?? value._seconds;
  if (typeof sec === 'number') {
    const ns = value.nanoseconds ?? value._nanoseconds ?? 0;
    return sec * 1000 + ns / 1e6;
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d.getTime();
}

/** Common timezone options for profile picker. */
export const TIMEZONE_OPTIONS = [
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern (US)' },
  { value: 'America/Los_Angeles', label: 'Pacific (US)' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Paris', label: 'Paris' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Asia/Dubai', label: 'Dubai' },
  { value: 'Australia/Sydney', label: 'Sydney' },
];

/**
 * Format UTC timestamp (ms) as date string (DD/MMM/YYYY) in given timezone.
 * @param {number} utcMs - UTC milliseconds
 * @param {string} [timezone] - IANA timezone (default Asia/Kolkata)
 */
export function formatDateInTimezone(utcMs, timezone = DEFAULT_TIMEZONE) {
  const ms = toUtcMs(utcMs);
  if (ms == null) return 'N/A';
  try {
    const dt = new Date(ms);
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    return formatter.format(dt);
  } catch {
    return 'N/A';
  }
}

/**
 * Format UTC timestamp (ms) as time string (HH:MM) in given timezone.
 * @param {number} utcMs - UTC milliseconds
 * @param {string} [timezone] - IANA timezone (default Asia/Kolkata)
 */
export function formatTimeInTimezone(utcMs, timezone = DEFAULT_TIMEZONE) {
  const ms = toUtcMs(utcMs);
  if (ms == null) return 'N/A';
  try {
    const dt = new Date(ms);
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return formatter.format(dt);
  } catch {
    return 'N/A';
  }
}

/**
 * Format UTC timestamp as date + time in given timezone.
 * @param {number} utcMs - UTC milliseconds
 * @param {string} [timezone] - IANA timezone
 * @param {{ useSeparator?: string }} [opts]
 */
export function formatDateTimeInTimezone(utcMs, timezone = DEFAULT_TIMEZONE, opts = {}) {
  const ms = toUtcMs(utcMs);
  if (ms == null) return 'N/A';
  const sep = opts.useSeparator ?? ', ';
  const d = formatDateInTimezone(ms, timezone);
  const t = formatTimeInTimezone(ms, timezone);
  return `${d}${sep}${t}`;
}

/**
 * Format scheduled match date + time. Uses timestamp for date only; time comes from scheduledTime
 * when present (API often stores date in timestamp and time slot separately).
 * @param {number|object} scheduledAtOrDate - UTC ms or Firestore-style timestamp
 * @param {string} [scheduledTime] - Time slot e.g. "18:30"
 * @param {string} [timezone] - IANA timezone
 * @param {{ useSeparator?: string }} [opts]
 */
export function formatScheduledDateTimeInTimezone(scheduledAtOrDate, scheduledTime, timezone = DEFAULT_TIMEZONE, opts = {}) {
  const ms = toUtcMs(scheduledAtOrDate);
  if (ms == null) return 'N/A';
  const sep = opts.useSeparator ?? ', ';
  const d = formatDateInTimezone(ms, timezone);
  const timeStr = (scheduledTime && typeof scheduledTime === 'string') ? scheduledTime.trim() : '';
  const t = timeStr || formatTimeInTimezone(ms, timezone);
  return `${d}${sep}${t}`;
}

/**
 * Get date-only key YYYY-MM-DD in given timezone (for filtering/comparison).
 * @param {number} utcMs - UTC milliseconds
 * @param {string} [timezone] - IANA timezone
 */
export function getDateKeyInTimezone(utcMs, timezone = DEFAULT_TIMEZONE) {
  const ms = toUtcMs(utcMs);
  if (ms == null) return '';
  try {
    const dt = new Date(ms);
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return formatter.format(dt); // YYYY-MM-DD
  } catch {
    return '';
  }
}

/** TZ to ISO offset for parseLocalToUTC (DST not handled for some TZs). */
const TZ_OFFSETS = {
  'Asia/Kolkata': '+05:30',
  UTC: 'Z',
  'America/New_York': '-05:00',
  'America/Los_Angeles': '-08:00',
  'Europe/London': '+00:00',
  'Europe/Paris': '+01:00',
  'Asia/Singapore': '+08:00',
  'Asia/Dubai': '+04:00',
  'Australia/Sydney': '+11:00',
};

/**
 * Parse local date + time in given timezone to UTC milliseconds.
 * @param {string} dateStr - YYYY-MM-DD
 * @param {string} timeStr - HH:MM or HH:MM:SS
 * @param {string} [timezone] - IANA timezone (default Asia/Kolkata)
 * @returns {number} UTC milliseconds
 */
export function parseLocalToUTC(dateStr, timeStr, timezone = DEFAULT_TIMEZONE) {
  if (!dateStr || !timeStr) return 0;
  const t = String(timeStr).trim();
  const [h = 0, m = 0] = t.split(':').map(Number);
  const offset = TZ_OFFSETS[timezone] ?? TZ_OFFSETS[DEFAULT_TIMEZONE];
  const iso = `${String(dateStr).trim()}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00${offset}`;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? 0 : d.getTime();
}
